import React from 'react';
import { Link } from 'react-router-dom';
import FooterLogoIcon from '../../assets/icons/footer-logo-icon.svg';

interface FooterColumnProps {
    title: string;
    items: Array<{
        label: string;
        href: string;
        color?: string;
    }>;
}

const FooterColumn: React.FC<FooterColumnProps> = ({ title, items }) => (
    <div className="flex flex-col space-y-2">
        <h3 className={`text-xl lg:text-2xl font-bold ${title === 'Mentoring' ? 'text-secondary-red' :
                title === 'Newsletter' ? 'text-secondary-blue' :
                    title === 'Blog' ? 'text-black dark:text-white' :
                        title === 'Data Founder' ? 'text-primary' :
                            'text-black dark:text-white'
            }`}>
            {title}
        </h3>
        <ul className="space-y-2">
            {items.map((item, index) => (
                <li key={index}>
                    <Link
                        to={item.href}
                        className={`text-base md:text-lg hover:opacity-80 ${item.color || 'text-neutral-500 dark:text-neutral-400'
                            }`}
                    >
                        {item.label}
                    </Link>
                </li>
            ))}
        </ul>
    </div>
);

export const Footer: React.FC = () => {
    const footerColumns: FooterColumnProps[] = [
        {
            title: 'Mentoring',
            items: [
                { label: 'I am a founder', href: '/mentoring/founder' },
                { label: 'I am a data lead', href: '/mentoring/data-lead' },
                { label: 'I am a mentor', href: '/mentoring/mentor' }
            ]
        },
        {
            title: 'Newsletter',
            items: [
                { label: 'I am a reader', href: '/newsletter/reader' },
                { label: 'I am a sponsor', href: '/newsletter/sponsor', color: 'text-neutral-400' }
            ]
        },
        {
            title: 'Blog',
            items: [
                { label: 'Datafounder', href: '/blog/datafounder' },
                { label: 'Use Cases', href: '/blog/use-cases' }
            ]
        },
        {
            title: 'Data Founder',
            items: [
                { label: 'About us', href: '/about' }
            ]
        }
    ];

    return (
        <footer className="container mx-auto w-full py-8 px-8 sm:px-6 lg:px-14 bg-white dark:bg-neutral-900">
            <div className="max-w-7xl">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 mb-12 justify-self-center place-content-center">
                    {footerColumns.map((column, index) => (
                        <FooterColumn key={index} {...column} />
                    ))}
                </div>

                {/* Social Links and Follow Us */}
                <div className="flex flex-col items-center">
                    <div
                        className="w-15 h-17 mb-2"
                        aria-hidden="true"
                    >
                        <img
                            src={FooterLogoIcon}
                            alt=""
                            className="w-full h-full object-contain"
                            loading="eager"
                            draggable={false}
                        />
                    </div>
                    <p className="text-neutral-500 dark:text-neutral-400 text-sm pb-1">Follow Us</p>
                    <div className="flex">
                        <a
                            href="https://linkedin.com"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200"
                        >
                            <span className="sr-only">LinkedIn</span>
                            <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                            </svg>
                        </a>
                    </div>
                </div>
            </div>
        </footer>
    );
};

Footer.displayName = 'Footer';